import React, {Fragment, ReactElement, useEffect, useState} from 'react';
import {Form, Formik} from 'formik';
import {isEmpty, isNull} from 'lodash';
import {useHistory, useLocation} from 'react-router-dom';
import {parseQueryString} from 'shared/utils';
import FormikTextField from 'src/components/FormikTextField';
import {useBoolean} from 'src/hooks/useBoolean';
import EducatorVerificationService, {
  PendingEducator,
} from 'src/services/EducatorVerificationService';
import Button from 'src/components/button';
import {Box} from '@mui/material';
import Table from 'src/components/SimpleTable/SimpleTable';
import Loading from 'shared/components/loading';
import VerifyButton from './VerifyButton';
import styles from './styles.module.scss';

const rowBuilder = (rows: [string, string][]) =>
  rows.map((row) => [{value: row[0]}, {value: <Box textAlign="left">{row[1]}</Box>}]);

export default function EducatorInspection(): ReactElement {
  const [educator, setEducator] = useState<PendingEducator | null>(null);
  const [lookupEmail, setLookupEmail] = useState<string>('');
  const loading = useBoolean(false);
  const location = useLocation();
  const history = useHistory();

  const getEducator = async () => {
    if (isEmpty(lookupEmail)) {
      setEducator(null);
      return;
    }

    try {
      loading.setTrue();
      const fetchedEducator = await EducatorVerificationService.show(lookupEmail);
      setEducator(fetchedEducator);
    } catch (err) {
      console.log(err);
      setEducator(null);
    } finally {
      loading.setFalse();
    }
  };

  const getQueryEmail = () => parseQueryString(location.search.slice(1)).email || '';

  useEffect(() => {
    getEducator();
  }, [lookupEmail]);

  useEffect(() => {
    setLookupEmail(getQueryEmail);
  }, [location.search]);

  const onEmailSubmit = ({email}) => {
    if (isEmpty(email)) return;

    history.push({
      search: `?email=${encodeURIComponent(email)}`,
      pathname: location.pathname,
    });
  };

  if (loading.value) {
    return <Loading />;
  }

  return (
    <>
      <Box my={4}>
        <h1 className="shared-font headline">Educator Inspection</h1>
      </Box>
      {isNull(educator) ? (
        <>
          {!isEmpty(getQueryEmail()) && (
            <div className="alert alert-warning">Educator not found</div>
          )}
          <Formik initialValues={{email: ''}} onSubmit={onEmailSubmit}>
            {({handleSubmit}) => (
              <Form onSubmit={handleSubmit}>
                <FormikTextField required name="email" placeholder="Educator email" />
                <Box mt={1}>
                  <Button type="submit">View Educator</Button>
                </Box>
              </Form>
            )}
          </Formik>
        </>
      ) : (
        <>
          <Box my={2}>
            <h3>User attributes</h3>
          </Box>
          <div>
            <Table>
              <Table.Body
                rows={rowBuilder([
                  ['Email', educator.email],
                  ['Institution', educator.institution.name],
                  ['Id', educator._id],
                  ['Onboarding completed at', educator.onboardingCompletedAt],
                  ['Created at', new Date(educator.createdAt * 1000).toString()],
                  ['Is verified', (!!educator.isVerified).toString()],
                  ['Name', `${educator.firstName} ${educator.lastName}`],
                  ['Email verified at', new Date(educator.emailVerifiedAt * 1000).toString()],
                  ['Is rejected', (!!educator.isRejected).toString()],
                  ['Website for employment', educator.websiteForEmployment],
                ])}
              />
            </Table>
          </div>
          {educator.officialIdUrl && (
            <div className="m-24">
              <img
                src={`https:${educator.officialIdUrl}`}
                className={styles.educatorInspectionId}
              />
            </div>
          )}
          {!educator.isVerified && (
            <Box display="flex" justifyContent="space-between" my={2}>
              <Box>
                <h3>Actions</h3>
              </Box>
              <VerifyButton educator={educator} buttonSize="medium" />
            </Box>
          )}
        </>
      )}
    </>
  );
}
